import React, { useRef } from 'react'
import { Horizontal, Vertical } from '@gravitate-js/excalibrr'
import { Skeleton } from 'antd'
import { DirectiveRow } from '../DirectiveRow'
import { IntradayTodayRow } from './Rows/IntradayTodayRow'
import { IntradayStatusRow } from './Rows/IntradayStatusRow'
import { List, CellMeasurer, CellMeasurerCache, AutoSizer } from 'react-virtualized'
import { useDirectiveContext } from '../../DirectiveContext'
import { sortTypes } from '../../constants'
import { MultiSortColumn } from '../Sorting/MultiSortColumn'
import { SortableColumn } from '../Sorting/SortableColumn'

export const IntradayGrid = ({
  isDailyLoading,
  dailyDirectives,
  activeSort,
  sortDirection,
  setActiveSort,
  setSortDirection,
  addPricesToRow,
}) => {
  const { viewingPlanned } = useDirectiveContext()
  return (
    <>
      <GridHeader
        viewingPlanned={viewingPlanned}
        activeSort={activeSort}
        sortDirection={sortDirection}
        setActiveSort={setActiveSort}
        setSortDirection={setSortDirection}
      />
      <Horizontal className='bg-1' flex={1}>
        <Vertical flex={1}>
          {isDailyLoading ? (
            <>
              <Skeleton className='p-4' active />
              <Skeleton className='p-4' active />
              <Skeleton className='p-4' active />
              <Skeleton className='p-4' active />
              <Skeleton className='p-4' active />
            </>
          ) : (
            <>
              <DirectiveRows addPricesToRow={addPricesToRow} dailyDirectives={dailyDirectives} />
            </>
          )}
        </Vertical>
      </Horizontal>
    </>
  )
}
const GridHeader = ({ viewingPlanned, activeSort, setActiveSort, sortDirection, setSortDirection }) => {
  const changeSort = (sortType) => {
    if (!sortType) {
      setActiveSort(undefined)
      setSortDirection(undefined)
      return
    }
    if (sortType === activeSort) {
      if (!sortDirection) {
        setSortDirection('asc')
      } else if (sortDirection === 'asc') {
        setSortDirection('desc')
      } else {
        setSortDirection(undefined)
      }
    } else {
      setSortDirection('asc')
    }
    setActiveSort(sortType)
  }

  return (
    <Horizontal flex={0} background='bg-2' border='bottom' className='px-3 directive-grid-header' verticalCenter>
      <MultiSortColumn
        title='DIRECTIVE'
        className='py-3 pl-3 sortable'
        align='left'
        changeSort={changeSort}
        activeSort={activeSort}
        sortDirection={sortDirection}
        sortableItems={[
          { title: 'Terminal', value: sortTypes.TERMINAL.key },
          { title: 'Supplier', value: sortTypes.SUPPLIER.key },
          { title: 'Product', value: sortTypes.PRODUCT.key },
        ]}
        flex={3}
      />
      <Vertical className='px-3' flex={4}>
        <Horizontal>
          <SortableColumn
            title='LOADED'
            className='sortable py-3'
            value={sortTypes.LOADED.key}
            changeSort={changeSort}
            activeSort={activeSort}
            sortDirection={sortDirection}
          />
          <SortableColumn
            title='SCHEDULED'
            className='sortable py-3'
            value={sortTypes.SCHEDULED.key}
            changeSort={changeSort}
            activeSort={activeSort}
            sortDirection={sortDirection}
          />
          {viewingPlanned && (
            <SortableColumn
              title='PLANNED'
              value={sortTypes.PLANNED.key}
              changeSort={changeSort}
              activeSort={activeSort}
              sortDirection={sortDirection}
            />
          )}
          <SortableColumn
            title='TOTAL'
            value={sortTypes.TOTAL.key}
            changeSort={changeSort}
            activeSort={activeSort}
            sortDirection={sortDirection}
          />
          <SortableColumn
            title='DIR. MIN'
            value={sortTypes.MIN.key}
            changeSort={changeSort}
            activeSort={activeSort}
            sortDirection={sortDirection}
          />
          <SortableColumn
            title='DIR. MAX'
            value={sortTypes.MAX.key}
            changeSort={changeSort}
            activeSort={activeSort}
            sortDirection={sortDirection}
          />
        </Horizontal>
      </Vertical>
    </Horizontal>
  )
}
const DirectiveRows = ({ dailyDirectives, addPricesToRow }) => {
  const cache = new CellMeasurerCache({
    defaultHeight: 60,
    fixedWidth: true,
  })

  const listRef = useRef(null)
  const resizeRow = (index) => {
    cache.clear(index)
    listRef.current?.recomputeRowHeights(index)
  }

  return (
    <AutoSizer>
      {({ width, height }) => (
        <List
          deferredMeasurementCache={cache}
          height={height}
          rowCount={dailyDirectives.length}
          rowHeight={cache.rowHeight}
          width={width}
          ref={listRef}
          overscanRowsCount={20}
          rowRenderer={({ index, key, parent, style }) => {
            return (
              <CellMeasurer cache={cache} columnIndex={0} key={key} parent={parent} rowIndex={index}>
                <div style={style}>
                  <DirectiveRow
                    addPricesToRow={addPricesToRow}
                    directive={dailyDirectives[index]}
                    resizeRow={() => resizeRow(index)}
                    dataRows={[
                      { title: '', height: 25, RowComponent: IntradayTodayRow },
                      { title: '', height: 30, RowComponent: IntradayStatusRow },
                    ]}
                  />
                </div>
              </CellMeasurer>
            )
          }}
        />
      )}
    </AutoSizer>
  )
}
